import { z } from 'zod/v4';

/**
 * Structured description of what the user is currently looking at in the app,
 * sent with every in-product agent message so the agent knows the on-screen
 * context without the user re-typing IDs (e.g. "this driver", "this assembly").
 *
 * Product-wide: the frontend `usePageContext()` hook derives this from the app's
 * route tree, so any route (projects, BOMs, sourcing, parts, PCB,
 * manufacturing, calculations, orders) populates it. Injected into the agent's
 * system prompt by the gateway.
 */
// This object is supplied by the BROWSER and ends up in the agent's SYSTEM
// prompt, so it is untrusted: length-bound every field at the parse boundary to
// reject oversized payloads, and the gateway additionally strips newlines /
// control characters before rendering it (see formatPageContextForPrompt) so it
// can't break out of its lines and inject instructions.
export const PageContextSchema = z.object({
    /**
     * The name of the route on screen, e.g. '/projects/$projectId/bom'. Bound like `route`.
     */
    pageType: z.string().max(512).optional(),
    /** Human-readable page/route title, used verbatim in the prompt. */
    pageTitle: z.string().max(120).optional(),
    /** The URL-encoded pathname on screen, without the query, e.g. '/projects/p%201/bom'. */
    route: z.string().max(512).optional(),
    /** The primary entity in view, if the route resolves to one. */
    entity: z
        .object({
            /** e.g. 'driver' | 'activity' | 'manufacturingAssemblyDetails' | 'manufacturingScenario'. */
            type: z.string().max(64),
            id: z.string().max(128),
            name: z.string().max(200).optional(),
        })
        .optional(),
    /** Extra identifier hints from the route (rfqId, manufacturingScenarioId, …). */
    ids: z.record(z.string().max(64), z.string().max(128)).optional(),
});

export type PageContext = z.infer<typeof PageContextSchema>;

/**
 * A file the user attached to the current agent turn.
 *
 * The client uploads the bytes via POST /gateway/llm/agent/stage-file (which
 * proxies to the MCP `code_mode_stage_file` tool in PUSH mode), gets back an
 * opaque `handle` and echoed metadata, and sends only that metadata on the next
 * chat send. The gateway injects a synthetic "[Attached: …]" line into the last
 * user message before `convertToModelMessages` so the model sees the attachment
 * in the conversation; the actual bytes never leave storage until the model
 * invokes `code_mode_exec` with `files: [{ path, handle }]`.
 */
export const AgentAttachmentSchema = z.object({
    /** Opaque staging handle minted by code_mode_stage_file — the only file-related value that enters the model context. */
    handle: z.string().min(1).max(512),
    /** Original filename as chosen by the user; shown in the injected marker and used for path fallback. */
    filename: z.string().min(1).max(256),
    /** Sanitized session-root path the agent should reference in code_mode_exec. */
    path: z.string().min(1).max(256),
    /** Reported byte count, best-effort — for UI/telemetry, not enforced. */
    byteCount: z.number().int().nonnegative().optional(),
});

export type AgentAttachment = z.infer<typeof AgentAttachmentSchema>;

/**
 * Response of POST /gateway/llm/agent/stage-file after a successful upload.
 */
export const AgentStageFileResponseSchema = z.object({
    handle: z.string().min(1),
    filename: z.string().min(1),
    path: z.string().min(1),
    byteCount: z.number().int().nonnegative(),
    /** RFC 3339 timestamp; the client should discard the chip past this. */
    expiresAt: z.string().min(1).optional(),
});

export type AgentStageFileResponse = z.infer<typeof AgentStageFileResponseSchema>;
